import React, { useEffect, useState } from "react";
import { adminGetPricing, adminUpdatePricing, getCurrencies } from "@/lib/api";
import { toast } from "sonner";
import { BadgeDollarSign, RefreshCw } from "lucide-react";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const inputCls = "w-full bg-secondary px-3 py-2.5 outline-none focus:ring-1 focus:ring-primary text-sm";

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [json, setJson] = useState("");
  const [jsonDirty, setJsonDirty] = useState(false);
  const [fx, setFx] = useState(null);

  useEffect(() => {
    adminGetPricing().then((d) => { setCfg(d); setJson(JSON.stringify(d, null, 2)); }).catch(() => toast.error("Load failed"));
    getCurrencies().then(setFx).catch(() => {});
  }, []);

  if (!cfg) return <p className="text-muted-foreground">Loading pricing…</p>;

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setPkg = (id, v) => setCfg((c) => ({ ...c, packages: c.packages.map((p) => (p.id === id ? { ...p, price_from: num(v) } : p)) }));
  const setCare = (id, v) => setCfg((c) => ({ ...c, care_plans: c.care_plans.map((p) => (p.id === id ? { ...p, monthly: num(v) } : p)) }));

  const save = async () => {
    let body = cfg;
    if (jsonDirty) {
      try { body = JSON.parse(json); } catch { toast.error("JSON is invalid — fix it or discard changes."); return; }
    }
    try {
      const saved = await adminUpdatePricing(body);
      setCfg(saved); setJson(JSON.stringify(saved, null, 2)); setJsonDirty(false);
      toast.success("Pricing updated — live on the site immediately.");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="admin-pricing" className="max-w-4xl space-y-10">
      <div>
        <h2 className="font-display text-3xl mb-2 flex items-center gap-3"><BadgeDollarSign className="w-7 h-7 text-primary" /> Pricing & Quote Calculator</h2>
        <p className="text-muted-foreground text-sm">Every price on the site is driven by this config — change a number here and it updates everywhere instantly. All prices are USD (base); visitors see live conversions.</p>
      </div>

      {/* Package prices */}
      <section className="border border-border p-6 bg-card">
        <p className="overline text-primary mb-5">Package starting prices (USD)</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {cfg.packages.map((p) => (
            <div key={p.id}>
              <label className="overline text-muted-foreground block mb-2">{p.name}</label>
              <input type="number" min="0" value={p.price_from} onChange={(e) => setPkg(p.id, e.target.value)} data-testid={`pkg-price-${p.id}`} className={inputCls} />
            </div>
          ))}
        </div>
      </section>

      {/* Care plans */}
      <section className="border border-border p-6 bg-card">
        <p className="overline text-primary mb-5">Website care plans (USD / month)</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {cfg.care_plans.map((p) => (
            <div key={p.id}>
              <label className="overline text-muted-foreground block mb-2">{p.name}</label>
              <input type="number" min="0" value={p.monthly} onChange={(e) => setCare(p.id, e.target.value)} data-testid={`care-price-${p.id}`} className={inputCls} />
            </div>
          ))}
        </div>
      </section>

      {/* Deposit + SEO fee */}
      <section className="border border-border p-6 bg-card grid sm:grid-cols-2 gap-4">
        <div>
          <label className="overline text-muted-foreground block mb-2">Deposit % of project total</label>
          <input type="number" min="1" max="100" value={cfg.deposit_percent} onChange={(e) => setField("deposit_percent", num(e.target.value))} data-testid="deposit-percent" className={inputCls} />
        </div>
        <div>
          <label className="overline text-muted-foreground block mb-2">SEO / launch setup fee (USD)</label>
          <input type="number" min="0" value={cfg.seo_setup_fee} onChange={(e) => setField("seo_setup_fee", num(e.target.value))} data-testid="seo-setup-fee" className={inputCls} />
        </div>
      </section>

      {/* Live FX */}
      {fx && (
        <section className="border border-border p-6 bg-card" data-testid="fx-rates">
          <p className="overline text-primary mb-3 flex items-center gap-2"><RefreshCw className="w-3.5 h-3.5" /> Live exchange rates (USD base)</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-sm">
            {Object.entries(fx.rates).map(([c, r]) => <span key={c} className="text-muted-foreground">{c} <b className="text-foreground">{r}</b></span>)}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Updated automatically every 12 hours. Conversions are labelled as indicative estimates on the site.</p>
        </section>
      )}

      {/* Advanced JSON editor */}
      <section className="border border-border p-6 bg-card">
        <p className="overline text-primary mb-3">Full configuration (advanced — JSON)</p>
        <p className="text-xs text-muted-foreground mb-4">Edit features, business types, sizes, design styles, timelines, maintenance tiers, package/care-plan feature lists, and the disclaimer text. Saving invalid JSON is blocked.</p>
        <textarea
          rows={18}
          value={json}
          onChange={(e) => { setJson(e.target.value); setJsonDirty(true); }}
          data-testid="pricing-json"
          className="w-full bg-secondary px-3 py-2.5 font-mono text-xs outline-none focus:ring-1 focus:ring-primary"
        />
      </section>

      <button onClick={save} data-testid="pricing-save" className="bg-primary text-white px-8 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
        Save Pricing
      </button>
    </div>
  );
}
